import { useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { getApiOrigin } from '../apiBase';

function readToken() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('token');
}

function isNegotiationAbort(err) {
    const msg = String(err?.message || err || '');
    return msg.includes('stopped during negotiation') || err?.name === 'AbortError';
}

export function useNotifications({ onNotification } = {}) {
    const [connection, setConnection] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [connected, setConnected] = useState(false);
    const onNotificationRef = useRef(onNotification);
    onNotificationRef.current = onNotification;

    const refreshUnread = useCallback(async () => {
        const token = readToken();
        if (!token) return;
        try {
            const origin = getApiOrigin();
            const res = await fetch(`${origin}/api/notifications/unread-count`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setUnreadCount(Number(data.count) || 0);
            }
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        const token = readToken();
        if (!token) {
            setConnection(null);
            setConnected(false);
            return undefined;
        }

        let disposed = false;
        const apiOrigin = getApiOrigin();
        const conn = new signalR.HubConnectionBuilder()
            .withUrl(`${apiOrigin}/hubs/notifications`, {
                accessTokenFactory: () => readToken() || '',
                transport:
                    signalR.HttpTransportType.WebSockets
                    | signalR.HttpTransportType.LongPolling,
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .build();

        const handleNotification = (n) => {
            if (disposed) return;
            setUnreadCount((prev) => prev + 1);
            onNotificationRef.current?.(n);
        };

        conn.on('Notification', handleNotification);
        conn.on('PendingNotifications', (items) => {
            if (disposed || !Array.isArray(items) || items.length === 0) return;
            setUnreadCount((prev) => prev + items.length);
            items.forEach((n) => onNotificationRef.current?.(n));
        });
        conn.on('ConfigUpdated', ({ key, value }) => {
            if (process.env.NODE_ENV === 'development' && !disposed) {
                console.log(`Config: ${key} = ${value}`);
            }
        });

        conn.onreconnected(() => {
            if (!disposed) setConnected(true);
        });
        conn.onclose(() => {
            if (!disposed) setConnected(false);
        });

        conn
            .start()
            .then(() => {
                if (disposed) {
                    conn.stop().catch(() => {});
                    return;
                }
                setConnected(true);
                if (process.env.NODE_ENV === 'development') {
                    console.log('WebSocket connected');
                }
            })
            .catch((err) => {
                if (disposed || isNegotiationAbort(err)) return;
                console.error('SignalR connect failed:', err);
                setConnected(false);
            });

        setConnection(conn);
        refreshUnread();

        return () => {
            disposed = true;
            conn.stop().catch(() => {});
            setConnection(null);
            setConnected(false);
        };
    }, [refreshUnread]);

    const markAllRead = useCallback(async () => {
        const token = readToken();
        if (!token) return;
        try {
            const origin = getApiOrigin();
            await fetch(`${origin}/api/notifications/mark-all-read`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            setUnreadCount(0);
        } catch {
            /* ignore */
        }
    }, []);

    return { unreadCount, connection, connected, markAllRead, refreshUnread };
}
